/**
 * Cart Upsell JavaScript
 * Handles the new button-based cart upsell system
 */

// Global flag to indicate script is loaded
window.cartUpsellScriptLoaded = true;

class CartUpsell {
  constructor() {
    this.isCheckingCart = false;
    this.isInitialLoad = true;
    this.init();
  }

  init() {
    // Check which upsell products are already in the cart on page load
    this.checkUpsellsInCart();
    
    // Setup event listeners for upsell buttons
    this.setupUpsellButtonListeners();
    
    // Listen for cart:item-added event
    this.setupCartEventListeners();
    
    // Initialize variant selects
    this.initializeVariantSelects();
    
    // Clean up sessionStorage if cart is empty
    this.cleanupSessionStorage();
  }

  setupCartEventListeners() {
    // Listen for cart:item-added event
    document.addEventListener('cart:item-added', (e) => {
      if (e.detail && e.detail.product) {
        this.handleItemAdded(e.detail.product);
      }
    });

    // Listen for cart:refresh event
    document.addEventListener('cart:refresh', () => {
      // Add a small delay to ensure loading states are cleared
      setTimeout(() => {
        this.checkUpsellsInCart();
        // Re-initialize variant selects after cart refresh
        setTimeout(() => {
          this.initializeVariantSelects();
        }, 50);
      }, 10);
    });

    // Listen for line-item:change:end event (cart item removal/quantity changes)
    document.addEventListener('line-item:change:end', (e) => {
      // Add a longer delay to ensure cart state is fully updated
      setTimeout(() => {
        this.checkUpsellsInCart();
        // Re-initialize variant selects after cart update
        setTimeout(() => {
          this.initializeVariantSelects();
        }, 50);
      }, 100);
    });
  }

  handleItemAdded(product) {
    let productHandle = '';
    let variantId = '';
    
    if (product.url) {
      const urlParts = product.url.split('/');
      productHandle = urlParts[urlParts.length - 1].split('?')[0];
    } else if (product.handle) {
      productHandle = product.handle;
    }
    
    if (product.variant_id) {
      variantId = product.variant_id.toString();
    } else if (product.id) {
      variantId = product.id.toString();
    }
    
    if (productHandle) {
      // Find and hide the corresponding upsell item
      const upsellItem = document.querySelector(
        `.cart-drawer--upsell-item[data-product-handle="${productHandle}"]`
      );
      if (upsellItem) {
        this.hideUpsellItem(upsellItem);
      }
    }
  }

  checkUpsellsInCart() {
    // Prevent multiple simultaneous cart checks
    if (this.isCheckingCart) {
      return;
    }
    
    this.isCheckingCart = true;
    
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Get all upsell items
        const upsellItems = document.querySelectorAll('.cart-drawer--upsell-item');
        let allItemsInCart = true;
        let hasLoadingItems = false;
        
        // Get list of upsell items that were previously added to cart
        const addedUpsells = JSON.parse(sessionStorage.getItem('addedUpsells') || '[]');
        
        upsellItems.forEach(upsellItem => {
          const productId = upsellItem.dataset.productId;
          
          // Check if this product is in the cart (regardless of properties)
          const itemInCart = cart.items.find(item => 
            item.product_id.toString() === productId
          );
          
          // If item is currently loading, mark it but don't process further
          if (upsellItem.classList.contains('loading')) {
            hasLoadingItems = true;
            allItemsInCart = false;
            return;
          }
          
          if (itemInCart) {
            // Hide the upsell item as it's already in the cart
            this.hideUpsellItem(upsellItem);
          } else {
            // Show the upsell item if it was previously added to cart and then removed
            if (addedUpsells.includes(productId) && upsellItem.classList.contains('in-cart')) {
              this.showUpsellItem(upsellItem);
              // Remove from addedUpsells since it's no longer in cart
              const updatedAddedUpsells = addedUpsells.filter(id => id !== productId);
              sessionStorage.setItem('addedUpsells', JSON.stringify(updatedAddedUpsells));
            } else if (this.isInitialLoad && !upsellItem.classList.contains('in-cart')) {
              // On initial load, show items that aren't in cart and aren't hidden
              this.showUpsellItem(upsellItem);
            }
            allItemsInCart = false;
          }
        });
        
        // If all upsell items are in the cart, hide the entire upsell container
        // But only if there are no loading items (to avoid premature hiding)
        if (allItemsInCart && upsellItems.length > 0 && !hasLoadingItems) {
          this.hideUpsellContainer();
        } else {
          this.showUpsellContainer();
        }
      })
      .catch(err => console.error('Error checking upsells in cart:', err))
      .finally(() => {
        this.isCheckingCart = false;
        this.isInitialLoad = false;
      });
  }

  setupUpsellButtonListeners() {
    // Use event delegation for upsell buttons
    document.addEventListener('click', (e) => {
      const upsellButton = e.target.closest('.product-form--upsell .cart-upsell-button');
      if (upsellButton) {
        e.preventDefault();
        this.handleUpsellButtonClick(upsellButton);
      }
    });

    // Handle form submissions for upsell products
    document.addEventListener('submit', (e) => {
      const productForm = e.target.closest('.product-form--upsell');
      if (productForm) {
        e.preventDefault();
        // Find the actual form element inside the product-form
        const form = productForm.querySelector('form');
        if (form) {
          this.handleUpsellFormSubmit(form);
        }
      }
    });

    // Handle variant selection changes
    document.addEventListener('change', (e) => {
      const variantSelect = e.target.closest('.variant-selects--upsell select');
      if (variantSelect) {
        this.handleVariantChange(variantSelect);
      }
    });
  }

  handleUpsellButtonClick(button) {
    const productForm = button.closest('.product-form--upsell');
    if (productForm) {
      // Find the actual form element inside the product-form
      const form = productForm.querySelector('form');
      if (form) {
        this.handleUpsellFormSubmit(form);
      }
    }
  }

  handleVariantChange(select) {
    const upsellItem = select.closest('.cart-drawer--upsell-item');
    if (!upsellItem) return;

    // Find the variants JSON in the upsell item
    const variantsScript = upsellItem.querySelector('script[type="application/json"]');
    let variants = [];
    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error('[Cart Upsell] Failed to parse variants JSON', e);
      }
    }

    // Gather selected options from THIS specific upsell item only
    const selects = upsellItem.querySelectorAll('.variant-selects--upsell select');
    const selectedOptions = Array.from(selects).map(s => s.value);

    // Find the matching variant
    let selectedVariant = null;
    for (let variant of variants) {
      if (variant.options && variant.options.length === selectedOptions.length && variant.options.every((opt, idx) => opt == selectedOptions[idx])) {
        selectedVariant = variant;
        break;
      }
    }

    // Update the hidden input with the correct variant ID
    const form = upsellItem.querySelector('.product-form--upsell');
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput && selectedVariant) {
      variantInput.value = selectedVariant.id;
    }

    // Update button state based on variant availability (only for this specific upsell item)
    const button = form.querySelector('.cart-upsell-button');
    if (button) {
      button.disabled = !(selectedVariant && selectedVariant.available);
    }

    // Update the upsell image if the variant has a different image
    this.updateUpsellImage(upsellItem, selectedVariant);
  }

  updateUpsellImage(upsellItem, selectedVariant) {
    if (!selectedVariant) return;

    const imageContainer = upsellItem.querySelector('.cart-upsell-image');
    if (!imageContainer) return;

    // Get the new image URL and create srcset
    let newImageUrl, newImageAlt;
    
    if (selectedVariant.featured_image) {
      newImageUrl = selectedVariant.featured_image.src;
      newImageAlt = selectedVariant.featured_image.alt || '';
    } else {
      // Fall back to product's default image if variant doesn't have a specific image
      const productId = upsellItem.dataset.productId;
      const productImage = imageContainer.querySelector('img');
      if (productImage) {
        // Keep the current image if variant doesn't have a specific image
        return;
      }
    }
    
    // Create srcset for different sizes (192x0 is the size used in the cart-upsell-item snippet)
    const newSrcset = `${newImageUrl} 192w, ${newImageUrl} 384w`;

    // Find the img element within the responsive-image structure
    const imgElement = imageContainer.querySelector('img');
    if (imgElement) {
      // Update the image source and alt text
      imgElement.src = newImageUrl;
      imgElement.alt = newImageAlt;
      
      // Update srcset
      imgElement.setAttribute('srcset', newSrcset);

      // Update data-src if it exists (for lazy loading)
      if (imgElement.hasAttribute('data-src')) {
        imgElement.setAttribute('data-src', newImageUrl);
      }

      // Update data-srcset if it exists (for lazy loading)
      if (imgElement.hasAttribute('data-srcset')) {
        imgElement.setAttribute('data-srcset', newSrcset);
      }

      // Remove lazy loading classes to show the image immediately
      imgElement.classList.remove('lazyload');
      imgElement.classList.add('no-blur');

      // Trigger any lazy loading libraries to reload the image
      if (window.lazyLoadInstance) {
        window.lazyLoadInstance.update();
      }
    }
  }

  initializeVariantSelects() {
    const variantSelects = document.querySelectorAll('.variant-selects--upsell');
    
    variantSelects.forEach(variantSelect => {
      // Enable all select elements within this variant select
      const selects = variantSelect.querySelectorAll('select');
      selects.forEach(select => {
        select.disabled = false;
        select.removeAttribute('data-originally-disabled');
      });
      
      // Initialize with the first available variant (without triggering button updates)
      const firstSelect = selects[0];
      if (firstSelect) {
        this.initializeVariantState(firstSelect);
      }
    });
  }

  initializeVariantState(select) {
    const upsellItem = select.closest('.cart-drawer--upsell-item');
    if (!upsellItem) return;

    // Find the variants JSON in the upsell item
    const variantsScript = upsellItem.querySelector('script[type="application/json"]');
    let variants = [];
    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error('[Cart Upsell] Failed to parse variants JSON', e);
      }
    }

    // Gather selected options
    const selects = upsellItem.querySelectorAll('.variant-selects--upsell select');
    const selectedOptions = Array.from(selects).map(s => s.value);

    // Find the matching variant
    let selectedVariant = null;
    for (let variant of variants) {
      if (variant.options && variant.options.length === selectedOptions.length && variant.options.every((opt, idx) => opt == selectedOptions[idx])) {
        selectedVariant = variant;
        break;
      }
    }

    // Update the hidden input with the correct variant ID
    const form = upsellItem.querySelector('.product-form--upsell');
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput && selectedVariant) {
      variantInput.value = selectedVariant.id;
    }

    // Update button state based on variant availability (only during initialization)
    const button = form.querySelector('.cart-upsell-button');
    if (button) {
      button.disabled = !(selectedVariant && selectedVariant.available);
    }

    // Update the upsell image to match the selected variant
    this.updateUpsellImage(upsellItem, selectedVariant);
  }

  handleUpsellFormSubmit(form) {
    const upsellItem = form.closest('.cart-drawer--upsell-item');
    if (!upsellItem) return;

    // Get the variant ID from the form
    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput || variantInput.disabled) return;

    const variantId = variantInput.value;
    const productId = upsellItem.dataset.productId;
    
    // Add loading state to THIS specific upsell item only
    this.addLoadingState(upsellItem);

    // Collect form data and format for /cart/add.js
    const formData = new FormData();
    
    // Add the variant ID and quantity
    formData.append('id', variantId);
    formData.append('quantity', '1');
    
    // Add the sections for rendering
    formData.append('sections', 'cart-drawer,cart-bubble,main-cart');
    formData.append('sections_url', window.location.pathname);
    
    // Add the cart tracking properties as line item properties
    const position = upsellItem.querySelector('input[name="properties[_cart-upsell_position]"]')?.value || '1';
    const timestamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
    
    // Use 'properties' to create line item properties
    formData.append('properties[_source]', 'cart-upsell');
    formData.append('properties[_cart-upsell_position]', position);
    formData.append('properties[_cart-upsell_added_at]', timestamp);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.status) {
        // Error from Shopify
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        this.showError(upsellItem, data.description || 'Failed to add item to cart');
        return;
      }
      

      
      // Success: immediately hide this specific upsell item
      this.hideUpsellItem(upsellItem);
      
      // Remove loading state before cart section updates
      this.removeLoadingState(upsellItem);
      
      // Update cart sections and dispatch cart:item-added
      if (window.renderCartSectionsFromResponse) {
        window.renderCartSectionsFromResponse(data);
      } else if (data.sections && Object.keys(data.sections).length > 0) {
        // Duplicate the renderContents logic from product.js
        const sectionMap = {
          'cart-drawer': {
            id: 'Cart-Drawer',
            selector: '.cart-drawer'
          },
          'cart-bubble': {
            id: 'cart-drawer-toggle',
            selector: '.thb-item-count'
          },
          'main-cart': {
            id: 'Cart',
            selector: '.thb-cart-form'
          }
        };
        Object.keys(sectionMap).forEach(key => {
          const section = sectionMap[key];
          if (data.sections[key] && document.getElementById(section.id)) {
            const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            // Defensive: only update if elementToReplace exists
            if (elementToReplace) {
              elementToReplace.innerHTML = new DOMParser().parseFromString(data.sections[key], 'text/html').querySelector(section.selector)?.innerHTML || data.sections[key];
            }
          }
        });
        // Optionally, re-initialize cart drawer behaviors if needed
        if (document.getElementById('Cart-Drawer')) {
          document.getElementById('Cart-Drawer').notesToggle?.();
          document.getElementById('Cart-Drawer').removeProductEvent?.();
        }
        
        // After cart sections are updated, restore upsell state
        setTimeout(() => {
          this.restoreUpsellStateAfterUpdate();
        }, 50);
              } else {
          // No sections available (common on localhost), fallback to cart refresh
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
        
        // After cart update, ensure the specific upsell item stays hidden
        setTimeout(() => {
          const updatedUpsellItem = document.querySelector(`.cart-drawer--upsell-item[data-product-id="${productId}"]`);
          if (updatedUpsellItem) {
            this.hideUpsellItem(updatedUpsellItem);
          }
          
          // Check if all upsell items are now in cart and hide container if needed
          this.checkUpsellsInCart();
        }, 200);
      
      // Dispatch cart:item-added event
      if (typeof dispatchCustomEvent === 'function') {
        dispatchCustomEvent('cart:item-added', {
          product: data.hasOwnProperty('items') ? data.items[0] : data
        });
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: {
            product: data.hasOwnProperty('items') ? data.items[0] : data
          },
          bubbles: true
        }));
      }
    })
    .catch(err => {
      console.error('Upsell Add Error:', err);
      this.removeLoadingState(upsellItem);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      this.showError(upsellItem, 'Failed to add item to cart');
    });
  }

  hideUpsellItem(upsellItem) {
    if (!upsellItem) return;
    
    const productId = upsellItem.dataset.productId;
    
    upsellItem.classList.add('in-cart');
    upsellItem.style.display = 'none';
    
    // Track that this item was added to cart
    if (productId) {
      const addedUpsells = JSON.parse(sessionStorage.getItem('addedUpsells') || '[]');
      if (!addedUpsells.includes(productId)) {
        addedUpsells.push(productId);
        sessionStorage.setItem('addedUpsells', JSON.stringify(addedUpsells));
      }
    }
  }

  showUpsellItem(upsellItem) {
    if (!upsellItem) return;
    upsellItem.classList.remove('in-cart');
    upsellItem.style.display = '';
  }

  hideUpsellContainer() {
    const upsellContainer = document.querySelector('[data-cart-upsells]');
    if (upsellContainer) {
      upsellContainer.style.display = 'none';
    }
  }

  showUpsellContainer() {
    const upsellContainer = document.querySelector('[data-cart-upsells]');
    if (upsellContainer) {
      upsellContainer.style.display = '';
    }
  }

  addLoadingState(upsellItem) {
    // Only add loading state to THIS specific upsell item
    if (!upsellItem) return;
    
    upsellItem.classList.add('loading');
    // Add loading class to the button itself
    const button = upsellItem.querySelector('.cart-upsell-button');
    if (button) button.classList.add('loading');
    
    // Disable only the form within THIS upsell item
    const form = upsellItem.querySelector('.product-form--upsell');
    if (form) {
      const inputs = form.querySelectorAll('input, button, select');
      inputs.forEach(input => {
        // Store original disabled state if not already stored
        if (!input.hasAttribute('data-originally-disabled')) {
          input.setAttribute('data-originally-disabled', input.disabled);
        }
        input.disabled = true;
      });
    }
  }

  removeLoadingState(upsellItem) {
    // Only remove loading state from THIS specific upsell item
    if (!upsellItem) return;
    
    upsellItem.classList.remove('loading');
    // Remove loading class from the button itself
    const button = upsellItem.querySelector('.cart-upsell-button');
    if (button) button.classList.remove('loading');
    
    // Re-enable only the form within THIS upsell item
    const form = upsellItem.querySelector('.product-form--upsell');
    if (form) {
      const inputs = form.querySelectorAll('input, button, select');
      inputs.forEach(input => {
        // Only re-enable if it wasn't originally disabled
        const wasOriginallyDisabled = input.getAttribute('data-originally-disabled') === 'true';
        if (!wasOriginallyDisabled) {
          input.disabled = false;
        }
        // Clean up the data attribute
        input.removeAttribute('data-originally-disabled');
      });
    }
  }

  showError(upsellItem, message) {
    // Find or create error message element
    let errorElement = upsellItem.querySelector('.upsell-error-message');
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'upsell-error-message tw:text-red-600 tw:text-sm tw:mt-2';
      const form = upsellItem.querySelector('.product-form--upsell');
      if (form) {
        form.appendChild(errorElement);
      }
    }
    
    errorElement.textContent = message;
    errorElement.style.display = 'block';
    
    // Hide error after 5 seconds
    setTimeout(() => {
      errorElement.style.display = 'none';
    }, 5000);
  }

  refreshCart() {
    // Trigger cart refresh event
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true
    }));
  }

  cleanupSessionStorage() {
    // Check if cart is empty and clear sessionStorage if so
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        if (cart.item_count === 0) {
          sessionStorage.removeItem('addedUpsells');
        }
      })
      .catch(err => console.error('Error checking cart for cleanup:', err));
  }

  restoreUpsellStateAfterUpdate() {
    // Get list of upsell items that were previously added to cart
    const addedUpsells = JSON.parse(sessionStorage.getItem('addedUpsells') || '[]');
    
    // Hide any upsell items that were previously added
    addedUpsells.forEach(productId => {
      const upsellItem = document.querySelector(`.cart-drawer--upsell-item[data-product-id="${productId}"]`);
      if (upsellItem) {
        this.hideUpsellItem(upsellItem);
      }
    });
  }

  // Removed showInitialUpsells method - logic now handled in checkUpsellsInCart
}

// Initialize cart upsell functionality when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  initializeCartUpsell();
});

// Also initialize when cart drawer is opened (for dynamic content)
document.addEventListener('cart-drawer:open', () => {
  // Small delay to ensure content is loaded
  setTimeout(() => {
    initializeCartUpsell();
  }, 100);
});

// Initialize immediately if DOM is already loaded
if (document.readyState === 'loading') {
  // DOM is still loading, wait for DOMContentLoaded
} else {
  // DOM is already loaded, initialize immediately
  initializeCartUpsell();
}

function initializeCartUpsell() {
  // Only initialize if we're in a cart context and not already initialized
  if (document.querySelector('.cart-drawer--upsell-item') && !window.cartUpsell) {
    window.cartUpsell = new CartUpsell();
  } else if (window.cartUpsell) {
    // Re-initialize if already exists
    window.cartUpsell.initializeVariantSelects();
    window.cartUpsell.checkUpsellsInCart();
  }
}

// Fallback: Check for upsell items periodically in case events don't fire
let upsellCheckInterval;
function startUpsellCheckInterval() {
  if (upsellCheckInterval) return; // Already running
  
  upsellCheckInterval = setInterval(() => {
    // Only check if we haven't initialized yet and upsell items exist
    if (!window.cartUpsell && document.querySelector('.cart-drawer--upsell-item')) {
      initializeCartUpsell();
      // Stop checking once initialized
      clearInterval(upsellCheckInterval);
      upsellCheckInterval = null;
    }
  }, 1000); // Check every second
  
  // Stop checking after 10 seconds to avoid infinite checking
  setTimeout(() => {
    if (upsellCheckInterval) {
      clearInterval(upsellCheckInterval);
      upsellCheckInterval = null;
    }
  }, 10000);
}

// Start the fallback check
startUpsellCheckInterval(); 